// Gatsby requirements
import React, { useState, useEffect, useCallback } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Image from "utils/Image"
import { storyblokEditable } from "gatsby-source-storyblok";

// Components
import RichText from "utils/RichTextRenderer"

// Styles
import * as CSS from './Reviews.module.scss'


const Reviews = ({ data }) => {

    // All of this enables the carousel and its navigation dots
    // I just copy pasted it from the docs: https://www.embla-carousel.com/api/
    const [viewportRef, embla] = useEmblaCarousel({
        align: "center",
        loop: "true",
        skipSnaps: false,
        draggable: data?.reviews?.length > 1 ? true : false
    });
    const [selectedIndex, setSelectedIndex] = useState(0);
  
    const scrollTo = useCallback((index) => embla && embla.scrollTo(index), [
      embla
    ]);
  
    const onSelect = useCallback(() => {
      if (!embla) return;
      setSelectedIndex(embla.selectedScrollSnap());
    }, [embla, setSelectedIndex]);
  
    useEffect(() => {
      if (!embla) return;
      onSelect();
      embla.on("select", onSelect);
    }, [embla, onSelect]);

    return (
        <section
            className={`
                ${CSS.section}
                ${'section' + data._uid}
                ${data?.style === 'New (Larger Image)' ? `${CSS.newLargerStyle} reviews-section` : ''}
                ${data?.marginTop === 'Small' ? CSS.marginTopSmall : data?.marginTop === 'Extra Small' ? CSS.marginTopExtraSmall : ''}
                ${data?.marginBottom === 'Small' ? CSS.marginBottomSmall : data?.marginBottom === 'Extra Small' ? CSS.marginBottomExtraSmall : ''}
            `}
            style={{backgroundColor: data?.backgroundColor}}
            {...storyblokEditable(data)}
        >

            {data?.css &&
                <style dangerouslySetInnerHTML={{__html:
                    data.css.replaceAll('.section', '.section' + data._uid).replaceAll('.top-text', '.toptext' + data._uid).replaceAll('.review', '.review' + data._uid)
                }} />
            }

            <style>
                {`.${CSS.reviews}::after, .${CSS.reviews}::before {background: linear-gradient(90deg, ${data?.backgroundColor} 0%, rgba(255,255,255,0) 100%);}`}
            </style>

            <div>

                <div className={`
                    ${CSS.topText}
                    ${(data?.totalRating === '0' || !data?.totalRating) ? CSS.noTotalRating : ''}
                    ${'toptext' + data._uid}
                `}>
                    {data?.totalRating && data?.totalRating !== '0' &&
                        <>
                            <p className={CSS.totalRatingWrapper}><span className={CSS.totalRating}>{data?.totalRating}</span> <span>Out Of <br/> 5 Stars</span></p>
                            <img
                                src="/icons/star-rating.svg"
                                className={`
                                    ${data?.totalRating > 4.25 && data?.totalRating <= 4.74 && CSS.rating45}
                                    ${data?.totalRating > 3.75 && data?.totalRating <= 4.24 && CSS.rating4}
                                    ${data?.totalRating > 3.25 && data?.totalRating <= 3.74 && CSS.rating35}
                                    ${data?.totalRating > 2.75 && data?.totalRating <= 3.24 && CSS.rating3}
                                    ${data?.totalRating > 2.25 && data?.totalRating <= 2.74 && CSS.rating25}
                                    ${data?.totalRating > 1.75 && data?.totalRating <= 2.24 && CSS.rating2}
                                    ${data?.totalRating > 1.25 && data?.totalRating <= 1.74 && CSS.rating15}
                                    ${data?.totalRating <= 1.24 && CSS.rating1}
                                `}
                                loading="lazy"
                                width="144"
                                height="20"
                                alt={`Star rating: ${data?.totalRating} stars`}
                            />
                        </>
                    }
                    {data?.textUnderRating && 
                        <RichText content={data?.textUnderRating} />
                    }
                </div>

                <div className={CSS.reviews}>
                    <div className="emabla" ref={viewportRef}>
                        <div className="embla__container">
                            {data?.reviews?.map((loop, i) => (
                                <div key={i} className={`
                                    ${CSS.review}
                                    ${data?.reviews?.length === 1 ? CSS.justOneReview : ''}
                                    ${selectedIndex === i ? CSS.active : ''}
                                    ${'review' + data._uid}
                                    embla__slide
                                `}>
                                    <div className={CSS.reviewText}>
                                        <RichText content={loop?.reviewText} />
                                        {data?.style === 'New (Larger Image)' &&
                                            <div>
                                                <div className={CSS.nameFlex}>
                                                    {data?.style === 'New (Larger Image)' && <img className={CSS.mobileImage} loading="lazy" height="75" width="75" src={`https:${loop?.personImage?.file?.url}?fit=thumb&f=top_left&h=100&w=100&r=180`} alt={loop?.personImage?.description}/> }
                                                    <div>
                                                        {loop?.name && <span className={CSS.personName}>{loop?.name}</span> }
                                                        {loop?.textUnderName && <span className={CSS.textUnderName}>{loop?.textUnderName}</span> }
                                                    </div>
                                                </div>
                                                {loop?.rating && <img
                                                    src="/icons/star-rating.svg"
                                                    className={`
                                                        ${CSS.starRating}
                                                        ${loop?.rating > 4.25 && loop?.rating <= 4.74 && CSS.rating45}
                                                        ${loop?.rating > 3.75 && loop?.rating <= 4.24 && CSS.rating4}
                                                        ${loop?.rating > 3.25 && loop?.rating <= 3.74 && CSS.rating35}
                                                        ${loop?.rating > 2.75 && loop?.rating <= 3.24 && CSS.rating3}
                                                        ${loop?.rating > 2.25 && loop?.rating <= 2.74 && CSS.rating25}
                                                        ${loop?.rating > 1.75 && loop?.rating <= 2.24 && CSS.rating2}
                                                        ${loop?.rating > 1.25 && loop?.rating <= 1.74 && CSS.rating15}
                                                        ${loop?.rating <= 1.24 && CSS.rating1}
                                                    `}
                                                    width="144"
                                                    height="20"
                                                    loading="lazy"
                                                    alt={`Star rating: ${loop?.rating} stars`}
                                                /> }
                                            </div>
                                        }
                                    </div>
                                    <div className={CSS.imageAndName}>
                                        {loop?.image?.filename &&
                                            <div className={CSS.image}>
                                                {data?.style !== 'New (Larger Image)' &&
                                                    <img
                                                        src={loop?.image?.filename + '/m/100x100/filters:quality(90)'}
                                                        width="75"
                                                        height="75"
                                                        alt={loop?.alt || ''}
                                                        loading="lazy"
                                                    />
                                                }
                                                {data?.style === 'New (Larger Image)' &&
                                                    <Image img={loop?.image} />
                                                }
                                            </div>
                                        }
                                        {data?.style !== 'New (Larger Image)' &&
                                            <div>
                                                {loop?.name && <span className={CSS.personName}>{loop?.name}</span> }
                                                {loop?.textUnderName && <span className={CSS.textUnderName}>{loop?.textUnderName}</span> }
                                                {loop?.rating &&
                                                    <img
                                                        src="/icons/star-rating.svg"
                                                        className={`
                                                            ${CSS.starRating}
                                                            ${loop?.rating > 4.25 && loop?.rating <= 4.74 && CSS.rating45}
                                                            ${loop?.rating > 3.75 && loop?.rating <= 4.24 && CSS.rating4}
                                                            ${loop?.rating > 3.25 && loop?.rating <= 3.74 && CSS.rating35}
                                                            ${loop?.rating > 2.75 && loop?.rating <= 3.24 && CSS.rating3}
                                                            ${loop?.rating > 2.25 && loop?.rating <= 2.74 && CSS.rating25}
                                                            ${loop?.rating > 1.75 && loop?.rating <= 2.24 && CSS.rating2}
                                                            ${loop?.rating > 1.25 && loop?.rating <= 1.74 && CSS.rating15}
                                                            ${loop?.rating <= 1.24 && CSS.rating1}
                                                        `}
                                                        width="144"
                                                        height="20"
                                                        loading="lazy"
                                                        alt={`Star rating: ${loop?.rating} stars`}
                                                    />
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {data?.reviews?.length > 1 &&
                        <div className="embla__dots">
                            {data?.reviews?.map((loop, i) => (
                                <button
                                    key={i}
                                    onClick={() => scrollTo(i)}
                                    className={`embla__dot ${i === selectedIndex ? "embla__dot_selected" : ""}`}
                                    aria-label={`Go to slide ${i + 1}`}
                                />
                            ))}
                        </div>
                    }

                </div>
            </div>
        </section>
    )
}

export default Reviews